import { useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { tokens } from "../theme";
import { useBookingScope } from "../hooks/useBookingScope";

/**
 * Always-visible active-business indicator + switcher, mounted in the shell
 * (Topbar). Single global control replacing the per-page business `<Select>`s
 * that each route used to re-implement (and that diverged from the global
 * `selectedBusinessId` for admins).
 *
 *   - Admin with >1 business → dropdown (writes `selectedBusinessId`).
 *   - Operator / single business → static label (no switching to do).
 *
 * Pages read scope via `useBookingScope().effectiveBusinessId`; they no
 * longer carry their own picker. Admin cross-business "all" oversight is a
 * separate, table-local toggle — not part of this everyday switcher.
 */
export default function BusinessSwitcher() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const {
    canPickBusiness,
    businessOptions,
    activeBusinessName,
    effectiveBusinessId,
    selectBusinessId,
  } = useBookingScope();

  // Sort once for predictable order in the popover — operators with many
  // businesses learn the position of their busiest ones; a stable order
  // makes that intuition usable. Case-insensitive locale compare so MN
  // characters sort the way operators read them. Memoized at the top so
  // hook order stays stable across the early-return branches below.
  const sorted = useMemo(
    () =>
      [...businessOptions].sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '', 'mn', { sensitivity: 'base' }),
      ),
    [businessOptions],
  );

  // Nothing to show until businesses load.
  if (businessOptions.length === 0) return null;

  if (!canPickBusiness) {
    // Single-business operator: just surface which business they're in.
    return (
      <Typography
        variant="body2"
        sx={{ display: "flex", alignItems: "center", gap: 0.75, fontWeight: 600 }}
        color={colors.grey[100]}
        noWrap
      >
        <StorefrontOutlinedIcon fontSize="small" />
        {activeBusinessName ?? businessOptions[0]?.name ?? "—"}
      </Typography>
    );
  }

  // Autocomplete with `disableClearable` requires value to be the option
  // type or undefined (not null), so fall back to the first option when
  // nothing is selected — matches the current default-pick behavior.
  const selected =
    sorted.find((b) => b.id === effectiveBusinessId) ?? sorted[0];

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 240 }}
      options={sorted}
      value={selected}
      onChange={(_, next) => selectBusinessId(next?.id ?? null)}
      getOptionLabel={(opt) => opt.name ?? ''}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      disableClearable
      openOnFocus
      // Default substring/contains filter (case-insensitive) is what
      // operators expect; no custom filterOptions needed.
      renderInput={(params) => (
        <TextField
          {...params}
          label="Бизнес"
          placeholder="Бизнес хайх..."
        />
      )}
    />
  );
}
